import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { IPaymentBaseQueryParams, IPaymentErrorResult, IPaymentStatus, PaymentStatus, PaymentStatusQueryMap } from 'libs/shared/src/models';
import { BehaviorSubject } from 'rxjs';
import { BasicPaymentProvider } from '../basic.pp';

interface IStripeQueryParams extends IPaymentBaseQueryParams {
  /** The order ID returned from the Stripe redirect */
  orderID: string;
  /** The transaction token used for the payment */
  stripeTransactionToken?: string;
}

@Injectable({
  providedIn: 'root'
})
export class StripePaymentProvider extends BasicPaymentProvider {
  // Services
  private readonly route = inject(ActivatedRoute);
  private readonly translateService = inject(TranslateService);

  // Properties & Signals
  /** Emits the result received from the payment provider */
  public override resultReceived: BehaviorSubject<IPaymentStatus | undefined> = new BehaviorSubject<IPaymentStatus | undefined>(undefined);

  // Methods
  /**
   * Initializes stripe payment provider and will check for a redirect back from stripe
   */
  override initializeProvider(): void {
    this.checkForRedirectBack();
  }

  /**
   * Opens a new window at stripe using the given transactionToken
   * @param transactionToken to use for identification
   * @param redirectUrl optional redirect url to use after payment
   */
  public override initializePayment(transactionToken: string, redirectUrl?: string): void {
    if (!redirectUrl || !transactionToken || transactionToken.length === 0) return;
    window.open(redirectUrl, '_self');
  }

  /**
   * Generates a user-friendly message based on the payment status and optional error result
   * @param status of the payment
   * @param result optional error result from the payment provider
   */
  override generateStatusMessage(status: IPaymentStatus, _result?: IPaymentErrorResult<unknown>): string | undefined {
    if (status.status === PaymentStatus.Failed) {
      return this.translateService.instant('paymentService.errors.generic');
    }
    return undefined;
  }

  /**
   * Checks if the current route has a redirect back from Stripe and emits the result
   */
  private checkForRedirectBack(): void {
    const urlParams = this.route.snapshot.queryParams as IStripeQueryParams;
    if (urlParams.stripeTransactionToken && urlParams.state) {
      this.resultReceived.next({
        transactionId: urlParams.stripeTransactionToken,
        status: PaymentStatusQueryMap[urlParams.state]
      });
    }
  }
}

